import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const DURATION_MS = 260
const EASING = 'cubic-bezier(.2, .8, .2, 1)'

/**
 * Anime la hauteur de la fenêtre quand l'étape change : on part de la hauteur
 * mesurée avant le changement et on glisse vers la nouvelle. La hauteur reste
 * libre ensuite (liste de fichiers qui grandit, erreurs qui s'affichent).
 *
 * Renvoie un ref en callback : la fenêtre est montée dans un portail, un rendu
 * après la modale, un ref objet ne serait pas encore rempli à l'abonnement.
 */
export function useAnimatedStepHeight(step: number) {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const lastHeight = useRef<number | null>(null)

  // La dernière hauteur connue, relevée après chaque changement de taille : au
  // moment où l'étape change, elle vaut encore celle de l'étape quittée.
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return undefined
    lastHeight.current = element.getBoundingClientRect().height
    const observer = new ResizeObserver(() => {
      lastHeight.current = element.getBoundingClientRect().height
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
      lastHeight.current = null
    }
  }, [element])

  useLayoutEffect(() => {
    const from = lastHeight.current
    if (!element || from === null || typeof element.animate !== 'function') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const to = element.getBoundingClientRect().height
    if (Math.abs(to - from) < 1) return
    element.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: DURATION_MS,
      easing: EASING,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  return setElement
}
